/**
 * Format-agnostic document source model for the Studio viewer.
 *
 * WHY THIS EXISTS
 * ---------------
 * `@cogo/source-document` ships two browser-safe extractors — `docx/extract`
 * (WS-D0b) and `hwpx/extract` — but only the DOCX one was ever reachable from
 * the interface: the viewer declared `accept=".docx"` and the HWPX extractor
 * had no package export entry at all, so no bundler could resolve it. This
 * module is the seam that makes ONE viewer able to render either, without
 * forking the block list, the pagination, or the locator.
 *
 * The two extractions are NOT identical, and this module refuses to pretend
 * they are:
 *
 *   • DOCX returns a flat `blocks[]` and NINE statistics.
 *   • HWPX returns `sections[]` (each with its own `blocks[]`) and FIVE.
 *
 * So the normalization flattens sections into one ordered block list (HWPX
 * anchors are already section-scoped — `sec0:para:12` — so flattening cannot
 * collide), and the stat tiles are built from a PER-FORMAT field list. A field
 * the format does not measure is absent, never rendered as a `0`. Inventing a
 * `hiddenCharacters: 0` for an HWPX document would be a fabricated measurement.
 *
 * The one place the shapes differ inside a block is `listLevel` / `numId`,
 * which OOXML numbering provides and OWPML (as extracted today) does not.
 * `null` is the extractors' own encoding of "this paragraph is not list
 * numbered", and the renderer only draws the list badge for a non-null value —
 * so mapping HWPX paragraphs with `null` states "not measured / not present"
 * in exactly the vocabulary the viewer already speaks.
 */

import type {
  DocxBlock,
  DocxExtraction,
  DocxExtractionStats,
} from "@cogo/source-document/docx/extract";
import type {
  HwpxBlock,
  HwpxExtraction,
  HwpxExtractionStats,
} from "@cogo/source-document/hwpx/extract";

export type DocumentSourceFormat = "docx" | "hwpx";

/** Extensions the Studio document entrance actually accepts, in `accept=` order. */
export const DOCUMENT_SOURCE_EXTENSIONS = [".docx", ".hwpx"] as const;

/** The literal `accept` attribute for every document picker in Studio. */
export const DOCUMENT_SOURCE_ACCEPT = DOCUMENT_SOURCE_EXTENSIONS.join(",");

export function documentFormatForFileName(
  fileName: string,
): DocumentSourceFormat | null {
  const lower = fileName.trim().toLowerCase();
  if (lower.endsWith(".docx")) return "docx";
  if (lower.endsWith(".hwpx")) return "hwpx";
  return null;
}

/** The extension we refused, for the message — `""` when the name has none. */
export function fileExtensionOf(fileName: string): string {
  const base = fileName.trim().toLowerCase().split(/[\\/]/).pop() ?? "";
  const dot = base.lastIndexOf(".");
  return dot > 0 ? base.slice(dot) : "";
}

// --- Statistic tiles ----------------------------------------------------------

export interface DocumentStatTile {
  key: string;
  label: string;
  value: number;
}

const DOCX_STAT_FIELDS: ReadonlyArray<{
  key: keyof DocxExtractionStats;
  label: string;
}> = [
  { key: "sections", label: "Sections" },
  { key: "paragraphs", label: "Paragraphs" },
  { key: "tables", label: "Tables" },
  { key: "characters", label: "Characters" },
  { key: "hiddenCharacters", label: "Hidden chars" },
  { key: "fieldInstructions", label: "Field codes" },
  { key: "imageRefs", label: "Image refs" },
  { key: "alternateContentBranchesSkipped", label: "Alt skipped" },
  { key: "unknownElements", label: "Unknown elements" },
];

/**
 * Five fields, not nine. The HWPX extractor does not measure hidden runs, field
 * instructions, image references or alternate-content branches, so those tiles
 * are ABSENT for an HWPX document rather than shown as zero.
 */
const HWPX_STAT_FIELDS: ReadonlyArray<{
  key: keyof HwpxExtractionStats;
  label: string;
}> = [
  { key: "sections", label: "Sections" },
  { key: "paragraphs", label: "Paragraphs" },
  { key: "tables", label: "Tables" },
  { key: "characters", label: "Characters" },
  { key: "unknownElements", label: "Unknown elements" },
];

export function docxStatTiles(stats: DocxExtractionStats): DocumentStatTile[] {
  return DOCX_STAT_FIELDS.map(({ key, label }) => ({
    key,
    label,
    value: stats[key],
  }));
}

export function hwpxStatTiles(stats: HwpxExtractionStats): DocumentStatTile[] {
  return HWPX_STAT_FIELDS.map(({ key, label }) => ({
    key,
    label,
    value: stats[key],
  }));
}

// --- Normalized model ---------------------------------------------------------

export interface DocumentViewerLoadedModel {
  ok: true;
  format: DocumentSourceFormat;
  fileName: string;
  byteSize: number;
  /** Ordered blocks in document order (HWPX sections flattened, in order). */
  blocks: DocxBlock[];
  /** Only the statistics this format actually measures. */
  stats: DocumentStatTile[];
  unknownElementNames: string[];
  /**
   * How many sections the block list was flattened from. `1` for DOCX (its
   * `sectPr` count is a page-setup fact, not a block partition, so the viewer
   * never claims section-scoped anchors for it) and the real section count for
   * HWPX — which is what makes a bare paragraph ordinal ambiguous there.
   */
  sectionCount: number;
}

/**
 * A refusal, not a partial. `errorCode` is a typed domain code from the
 * extractor (`corrupt-archive`, `invalid-container-type`, …) or one of this
 * module's own — never a JS error class name.
 */
export interface DocumentViewerErrorModel {
  ok: false;
  /** The format we ATTEMPTED, or null when the extension itself was refused. */
  format: DocumentSourceFormat | null;
  fileName: string;
  byteSize: number;
  errorCode: string;
  message: string;
}

export type DocumentViewerLoadResult =
  | DocumentViewerLoadedModel
  | DocumentViewerErrorModel;

function normalizeHwpxBlock(block: HwpxBlock): DocxBlock {
  if (block.kind === "table") return block;
  return {
    kind: "paragraph",
    ordinal: block.ordinal,
    text: block.text,
    isHeading: block.isHeading,
    headingLevel: block.headingLevel,
    headingPath: block.headingPath,
    // OWPML list numbering is not extracted today. `null` is the extractors'
    // shared encoding for "not list numbered", and the renderer draws no badge
    // for it — so this states absence instead of inventing a level.
    listLevel: null,
    numId: null,
    anchor: block.anchor,
  };
}

export function normalizeDocxExtraction(
  extraction: DocxExtraction,
  fileName: string,
  byteSize: number,
): DocumentViewerLoadedModel {
  return {
    ok: true,
    format: "docx",
    fileName,
    byteSize,
    blocks: extraction.blocks,
    stats: docxStatTiles(extraction.stats),
    unknownElementNames: extraction.unknownElementNames,
    sectionCount: 1,
  };
}

export function normalizeHwpxExtraction(
  extraction: HwpxExtraction,
  fileName: string,
  byteSize: number,
): DocumentViewerLoadedModel {
  const blocks: DocxBlock[] = [];
  for (const section of extraction.sections) {
    for (const block of section.blocks) blocks.push(normalizeHwpxBlock(block));
  }
  return {
    ok: true,
    format: "hwpx",
    fileName,
    byteSize,
    blocks,
    stats: hwpxStatTiles(extraction.stats),
    unknownElementNames: extraction.unknownElementNames,
    sectionCount: extraction.sections.length,
  };
}

function errorCodeOf(error: unknown): string {
  return error && typeof error === "object" && "code" in error
    ? String((error as { code: unknown }).code)
    : "unknown";
}

function errorMessageOf(error: unknown): string {
  return error instanceof Error
    ? error.message
    : String(error ?? "unknown error");
}

/**
 * Parse `bytes` IN THE BROWSER, dispatching on the file name's extension.
 *
 * Never throws: a corrupt, mislabelled or unsupported source becomes a typed
 * error model the UI renders as an explicit banner. Both extractors are
 * imported lazily via their browser-safe subpaths, so neither parser lands in
 * Studio's eager chunk and a `.docx` never downloads the HWPX parser.
 */
export async function loadDocumentSource(
  bytes: Uint8Array,
  fileName: string,
): Promise<DocumentViewerLoadResult> {
  const byteSize = bytes.byteLength;
  const format = documentFormatForFileName(fileName);
  if (!format) {
    const extension = fileExtensionOf(fileName);
    return {
      ok: false,
      format: null,
      fileName,
      byteSize,
      errorCode: "unsupported-format",
      message: extension
        ? `${extension} files cannot be read here. Supported: ${DOCUMENT_SOURCE_EXTENSIONS.join(", ")}.`
        : `This file has no recognized extension. Supported: ${DOCUMENT_SOURCE_EXTENSIONS.join(", ")}.`,
    };
  }

  try {
    if (format === "hwpx") {
      const { extractHwpxSource } = await import(
        "@cogo/source-document/hwpx/extract"
      );
      return normalizeHwpxExtraction(
        extractHwpxSource(bytes),
        fileName,
        byteSize,
      );
    }
    const { extractDocxSource } = await import(
      "@cogo/source-document/docx/extract"
    );
    return normalizeDocxExtraction(
      extractDocxSource(bytes),
      fileName,
      byteSize,
    );
  } catch (error) {
    return {
      ok: false,
      format,
      fileName,
      byteSize,
      errorCode: errorCodeOf(error),
      message: errorMessageOf(error),
    };
  }
}
